import React, { useEffect, useState } from 'react';
import { StyleSheet, View, Text, ScrollView, ActivityIndicator, TouchableOpacity, Modal, FlatList, Platform, Linking } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute, useNavigation } from '@react-navigation/native';
import { fetchMissionById } from '../store/slices/missionSlice';
import missionService from '../services/missionService';
import locationService from '../services/locationService';
import { getPilotAircrafts } from '../services/aircraftService';
import reportService from '../services/reportService';
import { Ionicons } from '@expo/vector-icons';
import MissionPointsSection from '../components/missions/MissionPointsSection';

const MissionDetailScreen = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const { currentMission, isLoading, error } = useSelector(state => state.missions);
  const { user } = useSelector(state => state.auth);
  const missionId = route.params?.missionId;
  
  const [modalVisible, setModalVisible] = useState(false);
  const [pilotAircrafts, setPilotAircrafts] = useState([]);
  const [loadingAircrafts, setLoadingAircrafts] = useState(false);
  const [assigningAircraft, setAssigningAircraft] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasPoints, setHasPoints] = useState(false);
  const [checkingPoints, setCheckingPoints] = useState(true);
  const [locationDetails, setLocationDetails] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [primaryReport, setPrimaryReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [generatingPoints, setGeneratingPoints] = useState(false);

  useEffect(() => {
    console.log('currentMission cambió:', currentMission);
    if (currentMission) {
      console.log('Tipo de location:', typeof currentMission.location);
      console.log('Valor de location:', currentMission.location);
      
      if (currentMission.location && typeof currentMission.location === 'number') {
        console.log('Cargando detalles para location ID:', currentMission.location);
        loadLocationDetails(currentMission.location);
      } else if (currentMission.location && typeof currentMission.location === 'object') {
        console.log('Location ya es un objeto:', currentMission.location);
      } else {
        console.log('Location no disponible o en formato no reconocido');
      }
    }
  }, [currentMission]);

  useEffect(() => {
    if (missionId) {
      dispatch(fetchMissionById(missionId));
      checkMissionPoints();
      loadPrimaryReport();
    }
  }, [dispatch, missionId, user]);
  
  // Cargar los detalles de la ubicación
  const loadLocationDetails = async (locationId) => {
    if (!locationId) return;
    
    try {
      setLoadingLocation(true);
      const details = await locationService.getLocationById(locationId);
      console.log('Detalles de ubicación cargados:', details);
      setLocationDetails(details);
    } catch (error) {
      console.error('Error al cargar detalles de la ubicación:', error);
    } finally {
      setLoadingLocation(false);
    }
  };
  
  // Verificar si la misión tiene puntos
  const checkMissionPoints = async () => {
    if (!missionId) return;
    
    try {
      setCheckingPoints(true);
      const pointsInfo = await missionService.hasMissionPoints(missionId);
      setHasPoints(pointsInfo.has_points);
    } catch (error) {
      console.error('Error al verificar puntos de la misión:', error);
      setHasPoints(false);
    } finally {
      setCheckingPoints(false);
    }
  };
  
  // Cargar el reporte primario de la misión
  const loadPrimaryReport = async () => {
    if (!missionId) return;
    
    try {
      setLoadingReport(true);
      const reports = await reportService.getReportsByMission(missionId);
      const primary = reports.find(report => report.is_primary);
      setPrimaryReport(primary || null);
    } catch (error) {
      console.error('Error al cargar reporte primario:', error);
      setPrimaryReport(null);
    } finally {
      setLoadingReport(false);
    }
  };
  
  // Cargar los aircraft asignados al piloto
  const loadPilotAircrafts = async () => {
    if (!user?.is_pilot) return;
    
    setLoadingAircrafts(true);
    try {
      const aircrafts = await getPilotAircrafts();
      console.log('Aircraft del piloto:', aircrafts);
      setPilotAircrafts(aircrafts);
    } catch (error) {
      console.error('Error al cargar los aircraft del piloto:', error);
    } finally {
      setLoadingAircrafts(false);
    }
  };
  
  // Asignar un aircraft a la misión
  const handleAssignAircraft = async (aircraftId) => {
    setAssigningAircraft(true);
    try {
      console.log('Asignando aircraft', aircraftId, 'a misión', missionId);
      const updatedMission = await missionService.assignAircraft(missionId, aircraftId);
      console.log('Misión actualizada:', updatedMission);
      // Recargar la misión para mostrar el nuevo aircraft
      dispatch(fetchMissionById(missionId));
      setModalVisible(false);
    } catch (error) {
      console.error('Error al asignar el aircraft:', error);
      alert('No se pudo asignar el aircraft a la misión');
    } finally {
      setAssigningAircraft(false);
    }
  };
  
  // Descargar archivo KMZ
  const handleDownloadKMZ = async () => {
    if (!missionId) return;
    
    // No intentar descargar si no hay puntos
    if (!hasPoints) {
      alert('La misión no tiene puntos para exportar');
      return;
    }
    
    setDownloading(true);
    try {
      await missionService.exportMissionKMZ(missionId);
      console.log('Archivo descargado correctamente');
    } catch (error) {
      console.error('Error al descargar el archivo KMZ:', error);
      
      // Mostrar mensaje de error
      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = function() {
          try {
            const errorData = JSON.parse(reader.result);
            alert(`Error: ${errorData.error || 'No se pudo descargar el archivo KMZ'}`);
          } catch (e) {
            alert('Error al descargar el archivo KMZ');
          }
        };
        reader.readAsText(error.response.data);
      } else {
        alert(error.message || 'Error al descargar el archivo KMZ');
      }
    } finally {
      setDownloading(false);
    }
  };

  // Formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatear el tiempo de vuelo estimado (de minutos a formato hh:mm)
  const formatFlightTime = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Determinar el color según el estado de la misión
  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return '#f39c12'; // Amarillo
      case 'IN_PROGRESS':
        return '#3498db'; // Azul
      case 'COMPLETED':
        return '#2ecc71'; // Verde
      case 'CANCELLED':
        return '#e74c3c'; // Rojo
      default:
        return '#7f8c8d'; // Gris
    }
  };
  
  // Determinar el color según el estado del reporte
  const getReportStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return '#f39c12'; // Amarillo
      case 'IN_PROGRESS':
        return '#3498db'; // Azul
      case 'COMPLETED':
        return '#2ecc71'; // Verde
      case 'REJECTED':
        return '#e74c3c'; // Rojo
      default:
        return '#7f8c8d'; // Gris
    }
  };
  
  // Obtener el texto según el estado del reporte
  const getReportStatusText = (status) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'IN_PROGRESS':
        return 'Procesando';
      case 'COMPLETED':
        return 'Completado';
      case 'REJECTED':
        return 'Error';
      default:
        return 'Desconocido';
    }
  };

  // Generar puntos automáticamente
  const handleGenerateAutoPoints = async () => {
    if (!missionId || !currentMission) return;
    
    setGeneratingPoints(true);
    try {
      const result = await missionService.generateAutoPoints(missionId, {
        front_lap: 80,
        side_lap: 70,
        altitude: 120,
        speed: 15,
        pattern: 'serpentine'
      });
      
      alert(`Éxito: ${result.message}`);
      
      // Recargar puntos de la misión
      await checkMissionPoints();
      
    } catch (error) {
      console.error('Error al generar puntos automáticos:', error);
      const errorMessage = error.response?.data?.error || 'Error al generar puntos automáticos';
      alert(`Error: ${errorMessage}`);
    } finally {
      setGeneratingPoints(false);
    }
  };
  
  // Abrir mapa con las coordenadas de la ubicación
  const openMap = async (locationId) => {
    try {
      console.log('Abriendo mapa para ubicación:', locationId);
      
      // Intentar obtener detalles de la ubicación
      const details = await locationService.getLocationById(locationId);
      console.log('Detalles de ubicación:', details);
      
      if (!details || (!details.geometry && !details.properties?.geometry)) {
        alert('No se pudieron obtener las coordenadas de la ubicación');
        return;
      }
      
      // Obtener las coordenadas
      let coordinates;
      if (details.geometry?.coordinates) {
        coordinates = details.geometry.coordinates[0];
      } else if (details.properties?.geometry?.coordinates) {
        coordinates = details.properties.geometry.coordinates[0];
      }
      
      if (!coordinates || !coordinates.length) {
        alert('No se encontraron coordenadas válidas');
        return;
      }
      
      // Calcular el centro
      let sumLat = 0;
      let sumLng = 0;
      coordinates.forEach(coord => {
        sumLng += coord[0];
        sumLat += coord[1];
      });
      const centerLat = sumLat / coordinates.length;
      const centerLng = sumLng / coordinates.length;
      
      // Crear URL y abrir
      const mapsUrl = `https://www.google.com/maps?q=${centerLat},${centerLng}`;
      console.log('URL del mapa:', mapsUrl);
      window.open(mapsUrl, '_blank');
    } catch (error) {
      console.error('Error al abrir el mapa:', error);
      alert('No se pudo abrir el mapa');
    }
  };

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#2c3e50" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centered}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  if (!currentMission) {
    return (
      <View style={styles.centered}>
        <Text>No se encontró la misión</Text>
      </View>
    );
  }

  // Verificar si la misión ya tiene una aeronave asignada
  const hasAircraft = currentMission.aircraft_details !== null && currentMission.aircraft_details !== undefined;
  
  // Mostrar el botón de descarga si el usuario es piloto o staff
  const showDownloadButton = user?.is_pilot || user?.is_staff;
  
  console.log('¿Mostrar botón de descarga?', showDownloadButton);
  console.log('Detalles de la misión:', currentMission);

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.missionId}>Misión #{currentMission.sid || 'N/A'}</Text>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(currentMission.status) }]}>
          <Text style={styles.statusText}>
            {currentMission.status === 'PENDING' ? 'Pendiente' :
             currentMission.status === 'IN_PROGRESS' ? 'En Progreso' :
             currentMission.status === 'COMPLETED' ? 'Completada' :
             currentMission.status === 'CANCELLED' ? 'Cancelada' : 'Desconocido'}
          </Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Información General</Text>
        
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <Ionicons name="business-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>Empresa:</Text>
          <Text style={styles.infoValue}>{currentMission.enterprise_name || 'No asignada'}</Text>
        </View>
        
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <Ionicons name="location-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>Ubicación:</Text>
          {currentMission.location ? (
            <View style={styles.locationContainer}>
              {loadingLocation ? (
                <>
                  <Text style={styles.infoValue}>Cargando ubicación...</Text>
                  <ActivityIndicator size="small" color="#3498db" />
                </>
              ) : (
                <>
                  <Text style={styles.infoValue}>
                    {currentMission.location_name || 
                     locationDetails?.properties?.name || 
                     locationDetails?.name || 
                     (typeof currentMission.location === 'object' ? 
                      (currentMission.location.name || currentMission.location.properties?.name) : 
                      (currentMission.location ? 'Ubicación ' + currentMission.location : 'No asignada'))}
                  </Text>
                  <TouchableOpacity 
                    style={styles.mapLink}
                    onPress={() => openMap(currentMission.location)}
                  >
                    <View style={styles.mapIconWrapper}>
                      <Ionicons name="map-outline" size={16} color="#3498db" />
                    </View>
                    <Text style={styles.mapLinkText}>Ver en mapa</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          ) : (
            <Text style={styles.infoValue}>No asignada</Text>
          )}
        </View>
        
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <Ionicons name="flag-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>Objetivos:</Text>
          <View style={styles.objectivesContainer}>
            {currentMission.objectives_details && currentMission.objectives_details.length > 0 ? (
              currentMission.objectives_details.map((objective, index) => (
                <View key={objective.id} style={styles.objectiveItem}>
                  <Text style={styles.objectiveName}>{objective.name}</Text>
                  <Text style={styles.objectiveDescription}>{objective.description}</Text>
                </View>
              ))
            ) : (
              <Text style={styles.criticalText}>Sin objetivo</Text>
            )}
          </View>
        </View>
        
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <Ionicons name="time-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>Tiempo est.:</Text>
          <Text style={styles.infoValue}>{formatFlightTime(currentMission.estimated_flight_time)}</Text>
        </View>
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Aeronave</Text>
          {user?.is_pilot && !hasAircraft && (
            <TouchableOpacity 
              style={styles.actionButton}
              onPress={() => {
                loadPilotAircrafts();
                setModalVisible(true);
              }}
            >
              <Ionicons name="add-circle-outline" size={24} color="#3498db" />
              <Text style={styles.actionButtonText}>Asignar Aircraft</Text>
            </TouchableOpacity>
          )}
        </View>
        
        {currentMission.aircraft_details ? (
          <>
            <View style={styles.infoRow}>
              <View style={styles.iconWrapper}>
            <Ionicons name="airplane-outline" size={18} color="#7f8c8d" />
          </View>
              <Text style={styles.infoLabel}>Nombre:</Text>
              <Text style={styles.infoValue}>{currentMission.aircraft_details.name}</Text>
            </View>
            
            <View style={styles.infoRow}>
              <View style={styles.iconWrapper}>
            <Ionicons name="hardware-chip-outline" size={18} color="#7f8c8d" />
          </View>
              <Text style={styles.infoLabel}>Modelo:</Text>
              <Text style={styles.infoValue}>{currentMission.aircraft_details.model}</Text>
            </View>
            
            <View style={styles.infoRow}>
              <View style={styles.iconWrapper}>
            <Ionicons name="barcode-outline" size={18} color="#7f8c8d" />
          </View>
              <Text style={styles.infoLabel}>Serial:</Text>
              <Text style={styles.infoValue}>{currentMission.aircraft_details.serial_number}</Text>
            </View>
          </>
        ) : (
          <Text style={styles.noDataText}>No hay información de aeronave disponible</Text>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Piloto</Text>
        
        {currentMission.pilot_details ? (
          <>
            <View style={styles.infoRow}>
              <View style={styles.iconWrapper}>
            <Ionicons name="person-outline" size={18} color="#7f8c8d" />
          </View>
              <Text style={styles.infoLabel}>Nombre:</Text>
              {currentMission.pilot_name && currentMission.pilot ? (
                <TouchableOpacity 
                  onPress={() => {
                    console.log('Click en piloto del detalle:', {
                      user_is_enterprise: user?.is_enterprise_user,
                      mission_status: currentMission.status,
                      pilot_id: currentMission.pilot,
                      mission_id: currentMission.id
                    });
                    if (user?.is_enterprise_user && currentMission.status === 'COMPLETED') {
                      navigation.navigate('PilotProfile', {
                        pilotId: currentMission.pilot,
                        missionId: currentMission.id,
                        pilotName: currentMission.pilot_name
                      });
                    }
                  }}
                  disabled={!user?.is_enterprise_user || currentMission.status !== 'COMPLETED'}
                >
                  <Text style={[
                    styles.infoValue, 
                    user?.is_enterprise_user && currentMission.status === 'COMPLETED' && styles.clickableText
                  ]}>
                    {currentMission.pilot_name}
                    {user?.is_enterprise_user && currentMission.status === 'COMPLETED' && ' (Click para reseña)'}
                  </Text>
                </TouchableOpacity>
              ) : (
                <Text style={styles.infoValue}>{currentMission.pilot_name}</Text>
              )}
            </View>
            
            <View style={styles.infoRow}>
              <View style={styles.iconWrapper}>
            <Ionicons name="mail-outline" size={18} color="#7f8c8d" />
          </View>
              <Text style={styles.infoLabel}>Email:</Text>
              <Text style={styles.infoValue}>{currentMission.pilot_details.user?.email || 'No disponible'}</Text>
            </View>
            
            <View style={styles.infoRow}>
              <View style={styles.iconWrapper}>
            <Ionicons name="call-outline" size={18} color="#7f8c8d" />
          </View>
              <Text style={styles.infoLabel}>Teléfono:</Text>
              <Text style={styles.infoValue}>{currentMission.pilot_details.phone || 'No disponible'}</Text>
            </View>
          </>
        ) : (
          <Text style={styles.noDataText}>No hay información de piloto disponible</Text>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Programación de Vuelo</Text>
        
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <Ionicons name="calendar-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>Fecha:</Text>
          <Text style={styles.infoValue}>
            {currentMission.scheduled_date ? 
              new Date(currentMission.scheduled_date + 'T12:00:00').toLocaleDateString('es-ES') : 
              'No programada'
            }
          </Text>
        </View>
        
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <Ionicons name="time-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>Inicio:</Text>
          <Text style={styles.infoValue}>
            {currentMission.scheduled_time_start ? 
              currentMission.scheduled_time_start.substring(0, 5) : 
              'No programada'
            }
          </Text>
        </View>
        
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <Ionicons name="time-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>Fin:</Text>
          <Text style={styles.infoValue}>
            {currentMission.scheduled_time_end ? 
              currentMission.scheduled_time_end.substring(0, 5) : 
              'No programada'
            }
          </Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Fechas del Sistema</Text>
        
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <Ionicons name="add-circle-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>Creada:</Text>
          <Text style={styles.infoValue}>{formatDate(currentMission.created_at)}</Text>
        </View>
        
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <Ionicons name="refresh-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>Actualizada:</Text>
          <Text style={styles.infoValue}>{formatDate(currentMission.updated_at)}</Text>
        </View>
      </View>
      
      {/* Botón para descargar KMZ */}
      {showDownloadButton && (
        <View style={styles.section}>
          {checkingPoints ? (
            <TouchableOpacity 
              style={[styles.downloadButton, styles.disabledButton]}
              disabled={true}
            >
              <ActivityIndicator size="small" color="white" style={styles.downloadIcon} />
              <Text style={styles.downloadButtonText}>
                Verificando puntos...
              </Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity 
              style={[styles.downloadButton, !hasPoints && styles.disabledButton]}
              onPress={handleDownloadKMZ}
              disabled={downloading || !hasPoints}
            >
              <Ionicons name="download-outline" size={24} color="white" style={styles.downloadIcon} />
              <Text style={styles.downloadButtonText}>
                {downloading ? 'Descargando...' : hasPoints ? 'Descargar para DJI Pilot 2' : 'Sin puntos para exportar'}
              </Text>
            </TouchableOpacity>
          )}
          {hasPoints ? (
            <Text style={styles.downloadHint}>
              Haz clic para descargar el archivo KMZ
            </Text>
          ) : !checkingPoints && (
            <Text style={styles.warningText}>
              Esta misión no tiene puntos. Añade puntos antes de exportar.
            </Text>
          )}
        </View>
      )}
      
      {/* Recomendaciones de cámara para el piloto */}
      {user?.is_pilot && hasPoints && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Configuración de Cámara Recomendada</Text>
          
          {/* Configuración según objetivos */}
          {currentMission.objectives_details && currentMission.objectives_details.map((objective) => {
            const objName = objective.name.toLowerCase();
            
            // RGB Imaging
            if (objName.includes('rgb')) {
              return (
                <View key={objective.id} style={styles.cameraConfig}>
                  <Text style={styles.configTitle}>📷 Cámara RGB</Text>
                  <View style={styles.configGrid}>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Modo:</Text>
                      <Text style={styles.configValue}>Manual</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>ISO:</Text>
                      <Text style={styles.configValue}>100-200 (sol) / 400-800 (nublado)</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Velocidad:</Text>
                      <Text style={styles.configValue}>1/500s - 1/1000s</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Apertura:</Text>
                      <Text style={styles.configValue}>f/2.8 - f/4.0</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Formato:</Text>
                      <Text style={styles.configValue}>RAW + JPEG</Text>
                    </View>
                  </View>
                </View>
              );
            }
            
            // Multispectral Imaging
            if (objName.includes('multispectral')) {
              return (
                <View key={objective.id} style={styles.cameraConfig}>
                  <Text style={styles.configTitle}>🌱 Cámara Multiespectral</Text>
                  <View style={styles.configGrid}>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Bandas:</Text>
                      <Text style={styles.configValue}>RGB + Red Edge + NIR</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Exposición:</Text>
                      <Text style={styles.configValue}>Auto (calibrada)</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Formato:</Text>
                      <Text style={styles.configValue}>TIFF</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Calibración:</Text>
                      <Text style={styles.configValue}>Panel reflectante antes/después</Text>
                    </View>
                  </View>
                </View>
              );
            }
            
            // Thermal Imaging
            if (objName.includes('thermal')) {
              return (
                <View key={objective.id} style={styles.cameraConfig}>
                  <Text style={styles.configTitle}>🌡️ Cámara Térmica</Text>
                  <View style={styles.configGrid}>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Resolución:</Text>
                      <Text style={styles.configValue}>640x512 o superior</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Rango temp:</Text>
                      <Text style={styles.configValue}>-10°C a +140°C</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Emissividad:</Text>
                      <Text style={styles.configValue}>0.95 (vegetación)</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Formato:</Text>
                      <Text style={styles.configValue}>TIFF radiométrico</Text>
                    </View>
                  </View>
                </View>
              );
            }
            
            // LiDAR Scanning
            if (objName.includes('lidar')) {
              return (
                <View key={objective.id} style={styles.cameraConfig}>
                  <Text style={styles.configTitle}>📰 LiDAR</Text>
                  <View style={styles.configGrid}>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Frecuencia:</Text>
                      <Text style={styles.configValue}>240,000 pts/seg</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Precisión:</Text>
                      <Text style={styles.configValue}>3cm vertical</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Rango:</Text>
                      <Text style={styles.configValue}>450m máximo</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Formato:</Text>
                      <Text style={styles.configValue}>LAS/LAZ</Text>
                    </View>
                  </View>
                </View>
              );
            }
            
            // Hyperspectral Imaging
            if (objName.includes('hyperspectral')) {
              return (
                <View key={objective.id} style={styles.cameraConfig}>
                  <Text style={styles.configTitle}>🌈 Cámara Hiperespectral</Text>
                  <View style={styles.configGrid}>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Bandas:</Text>
                      <Text style={styles.configValue}>400-1000nm (270 bandas)</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Resolución:</Text>
                      <Text style={styles.configValue}>2.8nm espectral</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Velocidad:</Text>
                      <Text style={styles.configValue}>Reducir a 8-12 m/s</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Formato:</Text>
                      <Text style={styles.configValue}>ENVI/HDF5</Text>
                    </View>
                  </View>
                </View>
              );
            }
            
            // Crop Spraying
            if (objName.includes('spraying')) {
              return (
                <View key={objective.id} style={styles.cameraConfig}>
                  <Text style={styles.configTitle}>💧 Sistema de Fumigación</Text>
                  <View style={styles.configGrid}>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Altura:</Text>
                      <Text style={styles.configValue}>3-5m sobre cultivo</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Velocidad:</Text>
                      <Text style={styles.configValue}>5-7 m/s</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Caudal:</Text>
                      <Text style={styles.configValue}>1-2 L/min</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Ancho spray:</Text>
                      <Text style={styles.configValue}>4-6m</Text>
                    </View>
                  </View>
                </View>
              );
            }
            
            // Crop Monitoring, Weed Detection, Disease Detection
            if (objName.includes('monitoring') || objName.includes('weed') || objName.includes('disease')) {
              return (
                <View key={objective.id} style={styles.cameraConfig}>
                  <Text style={styles.configTitle}>🔍 Monitoreo de Cultivos</Text>
                  <View style={styles.configGrid}>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Cámara:</Text>
                      <Text style={styles.configValue}>RGB + Multiespectral</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Resolución:</Text>
                      <Text style={styles.configValue}>Máxima disponible</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Overlap:</Text>
                      <Text style={styles.configValue}>85% frontal / 75% lateral</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Formato:</Text>
                      <Text style={styles.configValue}>RAW + TIFF</Text>
                    </View>
                  </View>
                </View>
              );
            }
            
            // Irrigation Assessment
            if (objName.includes('irrigation')) {
              return (
                <View key={objective.id} style={styles.cameraConfig}>
                  <Text style={styles.configTitle}>💧 Evaluación de Riego</Text>
                  <View style={styles.configGrid}>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Cámara:</Text>
                      <Text style={styles.configValue}>Térmica + RGB</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Hora vuelo:</Text>
                      <Text style={styles.configValue}>10:00-14:00 hrs</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Condiciones:</Text>
                      <Text style={styles.configValue}>Cielo despejado</Text>
                    </View>
                    <View style={styles.configItem}>
                      <Text style={styles.configLabel}>Formato:</Text>
                      <Text style={styles.configValue}>TIFF radiométrico</Text>
                    </View>
                  </View>
                </View>
              );
            }
            
            return null;
          })}
          
          {/* Configuración general de vuelo */}
          <View style={styles.cameraConfig}>
            <Text style={styles.configTitle}>✈️ Configuración de Vuelo</Text>
            <View style={styles.configGrid}>
              <View style={styles.configItem}>
                <Text style={styles.configLabel}>Altura:</Text>
                <Text style={styles.configValue}>{currentMission.objectives_details?.some(obj => obj.name.toLowerCase().includes('spraying')) ? '3-5m AGL' : '120m AGL'}</Text>
              </View>
              <View style={styles.configItem}>
                <Text style={styles.configLabel}>Velocidad:</Text>
                <Text style={styles.configValue}>{currentMission.objectives_details?.some(obj => obj.name.toLowerCase().includes('spraying')) ? '5-7 m/s' : currentMission.objectives_details?.some(obj => obj.name.toLowerCase().includes('hyperspectral')) ? '8-12 m/s' : '10-15 m/s'}</Text>
              </View>
              <View style={styles.configItem}>
                <Text style={styles.configLabel}>Overlap frontal:</Text>
                <Text style={styles.configValue}>{currentMission.objectives_details?.some(obj => obj.name.toLowerCase().includes('monitoring') || obj.name.toLowerCase().includes('weed') || obj.name.toLowerCase().includes('disease')) ? '85%' : '80%'}</Text>
              </View>
              <View style={styles.configItem}>
                <Text style={styles.configLabel}>Overlap lateral:</Text>
                <Text style={styles.configValue}>{currentMission.objectives_details?.some(obj => obj.name.toLowerCase().includes('monitoring') || obj.name.toLowerCase().includes('weed') || obj.name.toLowerCase().includes('disease')) ? '75%' : '70%'}</Text>
              </View>
              <View style={styles.configItem}>
                <Text style={styles.configLabel}>Gimbal:</Text>
                <Text style={styles.configValue}>-90° (nadir)</Text>
              </View>
            </View>
          </View>
          
          <View style={styles.configNote}>
            <Ionicons name="information-circle-outline" size={16} color="#3498db" />
            <Text style={styles.configNoteText}>
              Estas configuraciones están optimizadas para los objetivos de esta misión y garantizan la calidad de datos necesaria para el análisis.
            </Text>
          </View>
        </View>
      )}
      
      {/* Sección de reporte de análisis */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Reporte de Análisis</Text>
          {primaryReport && (
            <TouchableOpacity 
              style={styles.actionButton}
              onPress={() => navigation.navigate('ReportDetail', { reportId: primaryReport.id })}
            >
              <Ionicons name="document-text-outline" size={24} color="#3498db" />
              <Text style={styles.actionButtonText}>Ver Detalle</Text>
            </TouchableOpacity>
          )}
        </View>
        
        {loadingReport ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color="#3498db" />
            <Text style={styles.loadingText}>Cargando reporte...</Text>
          </View>
        ) : primaryReport ? (
          <View>
            <View style={styles.infoRow}>
              <View style={styles.iconWrapper}>
                <Ionicons name="checkmark-circle-outline" size={18} color="#27ae60" />
              </View>
              <Text style={styles.infoLabel}>Estado:</Text>
              <View style={[styles.statusBadge, { backgroundColor: getReportStatusColor(primaryReport.status) }]}>
                <Text style={styles.statusText}>{getReportStatusText(primaryReport.status)}</Text>
              </View>
            </View>
            
            <View style={styles.infoRow}>
              <View style={styles.iconWrapper}>
                <Ionicons name="calendar-outline" size={18} color="#7f8c8d" />
              </View>
              <Text style={styles.infoLabel}>Generado:</Text>
              <Text style={styles.infoValue}>{formatDate(primaryReport.created_at)}</Text>
            </View>
            
            <View style={styles.infoRow}>
              <View style={styles.iconWrapper}>
                <Ionicons name="settings-outline" size={18} color="#7f8c8d" />
              </View>
              <Text style={styles.infoLabel}>Tipo:</Text>
              <Text style={styles.infoValue}>{primaryReport.report_type === 'auto' ? 'Automático' : 'Manual'}</Text>
            </View>
            
            {primaryReport.tiles_url && (
              <View style={styles.infoRow}>
                <View style={styles.iconWrapper}>
                  <Ionicons name="map-outline" size={18} color="#27ae60" />
                </View>
                <Text style={styles.infoLabel}>Mapa:</Text>
                <Text style={styles.infoValue}>Disponible</Text>
              </View>
            )}
            
            {primaryReport.findings && (
              <View style={styles.infoRow}>
                <View style={styles.iconWrapper}>
                  <Ionicons name="analytics-outline" size={18} color="#27ae60" />
                </View>
                <Text style={styles.infoLabel}>Análisis:</Text>
                <Text style={styles.infoValue}>Completado</Text>
              </View>
            )}
            
            <TouchableOpacity 
              style={styles.reportButton}
              onPress={() => navigation.navigate('ReportDetail', { reportId: primaryReport.id })}
            >
              <Ionicons name="document-text" size={20} color="white" style={styles.reportIcon} />
              <Text style={styles.reportButtonText}>Ver Reporte Completo</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.noReportContainer}>
            <Ionicons name="document-outline" size={48} color="#bdc3c7" />
            <Text style={styles.noDataText}>No hay reporte de análisis disponible</Text>
            <Text style={styles.noReportHint}>
              El reporte se generará automáticamente después de procesar las imágenes de la misión.
            </Text>
          </View>
        )}
      </View>
      
      {/* Sección de puntos de la misión */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Puntos de la Misión</Text>
          {!hasPoints && !checkingPoints && (user?.is_staff || user?.is_enterprise_user) && (
            <TouchableOpacity 
              style={[styles.actionButton, generatingPoints && styles.disabledButton]}
              onPress={handleGenerateAutoPoints}
              disabled={generatingPoints}
            >
              {generatingPoints ? (
                <ActivityIndicator size="small" color="#3498db" />
              ) : (
                <Ionicons name="add-circle-outline" size={24} color="#3498db" />
              )}
              <Text style={styles.actionButtonText}>
                {generatingPoints ? 'Generando...' : 'Generar Automáticamente'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
        {checkingPoints ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color="#3498db" />
            <Text style={styles.loadingText}>Cargando puntos...</Text>
          </View>
        ) : hasPoints ? (
          <MissionPointsSection missionId={missionId} />
        ) : (
          <Text style={styles.noDataText}>Esta misión no tiene puntos definidos</Text>
        )}
      </View>
      
      {/* Modal para seleccionar un aircraft */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Seleccionar Aeronave</Text>
              <TouchableOpacity onPress={() => setModalVisible(false)}>
                <Ionicons name="close-circle-outline" size={24} color="#e74c3c" />
              </TouchableOpacity>
            </View>
            
            {loadingAircrafts ? (
              <ActivityIndicator size="large" color="#3498db" style={styles.modalLoading} />
            ) : pilotAircrafts.length > 0 ? (
              <FlatList
                data={pilotAircrafts}
                keyExtractor={(item) => item.id.toString()}
                renderItem={({ item }) => (
                  <TouchableOpacity 
                    style={styles.aircraftItem}
                    onPress={() => handleAssignAircraft(item.id)}
                    disabled={assigningAircraft}
                  >
                    <View>
                      <Text style={styles.aircraftName}>{item.name}</Text>
                      <Text style={styles.aircraftModel}>{item.manufacturer} {item.model}</Text>
                    </View>
                    <Ionicons name="chevron-forward-outline" size={20} color="#7f8c8d" />
                  </TouchableOpacity>
                )}
              />
            ) : (
              <Text style={styles.noDataText}>No tienes aeronaves asignadas</Text>
            )}
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    color: '#e74c3c',
    fontSize: 16,
    textAlign: 'center',
  },
  warningText: {
    color: '#e67e22',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 5,
    fontStyle: 'italic',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingBottom: 5,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e3f2fd',
    padding: 5,
    borderRadius: 5,
  },
  actionButtonText: {
    color: '#3498db',
    marginLeft: 5,
    fontSize: 14,
  },
  downloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2ecc71',
    padding: 15,
    borderRadius: 5,
  },
  disabledButton: {
    backgroundColor: '#95a5a6',
    opacity: 0.7,
  },
  downloadButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
  },
  downloadIcon: {
    marginRight: 10,
  },
  downloadHint: {
    textAlign: 'center',
    color: '#7f8c8d',
    fontSize: 12,
    marginTop: 5,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    backgroundColor: 'white',
    margin: 20,
    borderRadius: 10,
    padding: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingBottom: 10,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  modalLoading: {
    padding: 20,
  },
  aircraftItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  aircraftName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  aircraftModel: {
    fontSize: 14,
    color: '#7f8c8d',
    marginTop: 5,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  missionId: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  statusBadge: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 5,
  },
  statusText: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
  },
  section: {
    backgroundColor: 'white',
    padding: 15,
    marginTop: 10,
    borderRadius: 5,
    marginHorizontal: 10,
    marginBottom: 5,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  iconWrapper: {
    marginRight: 8,
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mapIconWrapper: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoLabel: {
    width: 80,
    fontSize: 16,
    color: '#7f8c8d',
  },
  infoValue: {
    flex: 1,
    fontSize: 16,
    color: '#2c3e50',
  },
  noDataText: {
    color: '#7f8c8d',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: 10,
  },
  locationContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  mapLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
  },
  mapLinkText: {
    color: '#3498db',
    marginLeft: 5,
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  mapButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3498db',
    padding: 15,
    borderRadius: 5,
  },
  mapButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
  },
  mapIcon: {
    marginRight: 10,
  },
  mapHint: {
    textAlign: 'center',
    color: '#7f8c8d',
    fontSize: 12,
    marginTop: 5,
  },
  loadingContainer: {
    alignItems: 'center',
    padding: 20,
  },
  clickableText: {
    color: '#3498db',
    textDecorationLine: 'underline',
  },
  objectivesContainer: {
    flex: 1,
  },
  objectiveItem: {
    marginBottom: 8,
    paddingLeft: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#3498db',
  },
  objectiveName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  objectiveDescription: {
    fontSize: 13,
    color: '#7f8c8d',
    marginTop: 2,
  },
  criticalText: {
    color: '#e74c3c',
    fontWeight: 'bold',
  },
  cameraConfig: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#3498db',
  },
  configTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  configGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  configItem: {
    width: '48%',
    marginBottom: 8,
  },
  configLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#7f8c8d',
    marginBottom: 2,
  },
  configValue: {
    fontSize: 13,
    color: '#2c3e50',
    fontWeight: '500',
  },
  configNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#e3f2fd',
    padding: 10,
    borderRadius: 6,
    marginTop: 10,
  },
  configNoteText: {
    flex: 1,
    fontSize: 12,
    color: '#1976d2',
    marginLeft: 8,
    lineHeight: 16,
  },
  reportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3498db',
    padding: 12,
    borderRadius: 5,
    marginTop: 10,
  },
  reportButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 14,
  },
  reportIcon: {
    marginRight: 8,
  },
  noReportContainer: {
    alignItems: 'center',
    padding: 20,
  },
  noReportHint: {
    textAlign: 'center',
    color: '#7f8c8d',
    fontSize: 12,
    marginTop: 10,
    fontStyle: 'italic',
  },
  loadingText: {
    marginLeft: 10,
    color: '#3498db',
    fontSize: 14,
  },
});

export default MissionDetailScreen;